import React, { useMemo, useRef, useState, useEffect } from "react";
import { useListForHomeQuery } from "../../../api/banners.api";
import LoadingComponent from "../../../common/Loading/Loading.component";
import { mediaPreloader } from "../../../utils/preloader";
import "./ActiveBanner.css";

// Individual Banner Row with IntersectionObserver for smooth playback
const ActiveBannerRow = React.memo(({ item, index }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isInView, setIsInView] = useState(true);

  // Play/pause video based on viewport proximity
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        rootMargin: "350px 0px",
        threshold: 0.05,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Conserve device memory & mobile battery by pausing off-screen videos
  useEffect(() => {
    if (item.category === "video" && videoRef.current) {
      if (isInView) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  }, [isInView, item.category]);

  return (
    <article
      ref={containerRef}
      className="active-banner-row"
      aria-label={item.title || "Featured Drop"}
    >
      {/* 1. FULL DISPLAY BACKGROUND MEDIA */}
      <div className="active-banner-media-layer">
        {item.category === "video" ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            {item.mobileVideo && (
              <source media="(max-width: 768px)" src={item.mobileVideo} />
            )}
            <source src={item.desktopVideo || item.mobileVideo} />
          </video>
        ) : (
          <picture>
            {item.mobileImage && (
              <source media="(max-width: 768px)" srcSet={item.mobileImage} />
            )}
            <img
              src={item.desktopImage || item.mobileImage}
              alt={item.title || "Featured Product"}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </picture>
        )}
      </div>

      {/* 2. SOFT NATURAL VIGNETTE (NO MUDDY BLACK MESH) */}
      <div className="active-banner-scrim" aria-hidden="true" />

      {/* 3. TOP CORNER ACCENT / ARCHIVE TAG */}
      <div className="active-banner-top-bar">
        {/* <div className="active-banner-edition">
          <span className="active-banner-edition-dot" aria-hidden="true" />
          <span>{`DROP 0${index + 2}`}</span>
        </div> */}
        {/* <span className="active-banner-watermark">MAYA ARCHIVE</span> */}
      </div>

      {/* 4. BOTTOM EDITORIAL SPLIT BAR */}
      <div className="active-banner-bottom-bar">
        <div className="active-banner-split-container">
          {/* Left Column: Subtitle + Title */}
          <div className="active-banner-lead">
            {item.subtitle && (
              <div className="active-banner-kicker">
                <span className="active-banner-subtitle">
                  {item.subtitle}
                </span>
              </div>
            )}
            {item.title && (
              <h2 className="active-banner-title">{item.title}</h2>
            )}
          </div>

          {/* Right Column: Description + Luxury CTA */}
          <div className="active-banner-action-block">
            {item.content && (
              <p className="active-banner-desc">{item.content}</p>
            )}

            {item.button && (
              <a
                href={item.link || "#"}
                className="active-banner-cta"
              >
                <span className="active-banner-cta-label">
                  {item.button}
                </span>
                <span className="active-banner-cta-circle">
                  <svg
                    className="active-banner-cta-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="7" y1="17" x2="17" y2="7" />
                    <polyline points="7 7 17 7 17 17" />
                  </svg>
                </span>
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
});

ActiveBannerRow.displayName = "ActiveBannerRow";

const ActiveBanner = () => {
  const { data, isLoading } = useListForHomeQuery(undefined, {
    refetchOnMountOrArgChange: false,
    skip: false,
  });

  // Extract banners starting from index 1 (Index 0 is the Hero section)
  const banners = useMemo(() => {
    return data?.result ? data.result.slice(1) : [];
  }, [data]);

  // Instant Preload: pre-fetches all media into browser cache immediately
  useEffect(() => {
    if (banners && banners.length > 0) {
      const assets = [];
      banners.forEach((b) => {
        if (b.category === "image") {
          if (b.desktopImage) assets.push({ type: "image", src: b.desktopImage, priority: "high" });
          if (b.mobileImage) assets.push({ type: "image", src: b.mobileImage, priority: "high" });
        } else {
          if (b.desktopVideo) assets.push({ type: "video", src: b.desktopVideo });
          if (b.mobileVideo) assets.push({ type: "video", src: b.mobileVideo });
        }
      });
      if (assets.length > 0) {
        mediaPreloader.batchPreload(assets);
      }
    }
  }, [banners]);

  if (isLoading && !data) {
    return (
      <LoadingComponent
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "40vh",
          backgroundColor: "#0c0b0a",
        }}
      />
    );
  }

  // Render nothing if no secondary active banners exist
  if (!banners || banners.length === 0) {
    return null;
  }

  return (
    <section className="active-banners-section" aria-label="Featured Drops">
      {banners.map((item, index) => (
        <ActiveBannerRow
          key={item._id || index}
          item={item}
          index={index}
        />
      ))}
    </section>
  );
};

export default React.memo(ActiveBanner);
